import { z } from 'zod';
import type * as Y from 'yjs';

export const BACKGROUND_MODES = ['dots', 'grid', 'lines', 'none'] as const;
const color = z.string().regex(/^#[0-9a-f]{6}$/i);
const background = z.object({ mode: z.enum(BACKGROUND_MODES), color, patternColor: color });
const grid = z.object({ snap: z.boolean(), size: z.number().finite().min(4).max(400) });

/** Board settings shared by everyone on the board (`meta` map), outside the undo stack. */
export type BoardMeta = { background: z.infer<typeof background>; grid: z.infer<typeof grid> };

/** White with dots whatever the UI theme (backlog §3). */
/** A quiet light dot grid (P5e, FigJam-like calm): white sections and frames stand out on it. */
export const DEFAULT_BACKGROUND: BoardMeta['background'] = { mode: 'dots', color: '#f7f7f5', patternColor: '#dcdcd8' };
export const DARK_BACKGROUND: BoardMeta['background'] = { mode: 'dots', color: '#1e1e1e', patternColor: '#3f3f46' };
export const DEFAULT_GRID: BoardMeta['grid'] = { snap: false, size: 24 };

/** Never throws: a missing or invalid value (older board, forged peer) reads as the default. */
export function readMeta(meta: Y.Map<unknown>): BoardMeta {
  const bg = background.safeParse(meta.get('background'));
  const g = grid.safeParse(meta.get('grid'));
  return { background: bg.success ? bg.data : DEFAULT_BACKGROUND, grid: g.success ? g.data : DEFAULT_GRID };
}

export const isBoardMetaPatch = (p: Partial<BoardMeta>) =>
  (p.background === undefined || background.safeParse(p.background).success) && (p.grid === undefined || grid.safeParse(p.grid).success);
